import {
  useGetAllFeeTypesQuery,
  useAddFeeTypeMutation,
  useGetFeeTypeByIdQuery,
  useUpdateFeeTypeMutation,
  useDeleteFeeTypeMutation,
} from "../api/feeTypeApi";
import { FeeTypeResponseType } from "../types/feeTypeResponse";
import { FeeTypePayloadType } from "../types/feeTypeRequest";
import { getApiErrorMessage } from "../../../core/utils/errorHandler";
import { showToast } from "../../../core/utils/alert";
import { PaginationProps } from "../../../core/data/interface";

export const useFeeType = (params?: PaginationProps) => {
  const { data, isLoading, isError, error } = useGetAllFeeTypesQuery(
    params || {}
  );

  const [
    addFeeTypeMutation,
    { isLoading: isAdding, isError: isAddError, error: addError },
  ] = useAddFeeTypeMutation();

  const [
    updateFeeTypeMutation,
    { isLoading: isUpdating, isError: isUpdateError, error: updateError },
  ] = useUpdateFeeTypeMutation();

  const [
    deleteFeeTypeMutation,
    { isLoading: isDeleting, isError: isDeleteError, error: deleteError },
  ] = useDeleteFeeTypeMutation();

  const paginationData = {
    totalPages: data?.data?.totalPages,
    pageSize: data?.data?.pageSize,
    totalItems: data?.data?.totalItems,
    currentPage: data?.data?.currentPage,
  };

  const errorMessage = isError ? getApiErrorMessage(error) : null;
  const addErrorMessage = isAddError ? getApiErrorMessage(addError) : null;
  const updateErrorMessage = isUpdateError
    ? getApiErrorMessage(updateError)
    : null;
  const deleteErrorMessage = isDeleteError
    ? getApiErrorMessage(deleteError)
    : null;

  const addNewFeeType = async (payload: FeeTypePayloadType) => {
    try {
      const response = await addFeeTypeMutation(payload).unwrap();
      showToast({
        title: "Success",
        text: "FeeType added successfully!",
        icon: "success",
        timer: 3000,
      });
      return { success: true, data: response.data };
    } catch (error) {
      showToast({
        title: "Error",
        text: addErrorMessage || "Failed to add feetype",
        icon: "error",
        timer: 3000,
      });
      return {
        success: false,
        error: addErrorMessage || "Failed to add feetype",
      };
    }
  };

  const useGetFeeTypeById = (id: string) => {
    const { data, isLoading } = useGetFeeTypeByIdQuery({ id }, { skip: !id });
    return { feeType: data?.data?.feetype, isLoading, isError };
  };

  const updateFeeType = async (id: string, payload: FeeTypePayloadType) => {
    try {
      const response = await updateFeeTypeMutation({ id, payload }).unwrap();
      showToast({
        title: "Success",
        text: "FeeType updated successfully!",
        icon: "success",
        timer: 3000,
      });
      return { success: true, data: response.data };
    } catch (error) {
      showToast({
        title: "Error",
        text: updateErrorMessage || "Failed to update feetype",
        icon: "error",
        timer: 3000,
      });
      return {
        success: false,
        error: updateErrorMessage || "Failed to update feetype",
      };
    }
  };

  const deleteFeeType = async (id: string) => {
    try {
      const response = await deleteFeeTypeMutation({ id }).unwrap();
      showToast({
        title: "Success",
        text: "FeeType deleted successfully!",
        icon: "success",
        timer: 3000,
      });
      return { success: true, data: response };
    } catch (error) {
      showToast({
        title: "Error",
        text: deleteErrorMessage || "Failed to delete feetype",
        icon: "error",
        timer: 3000,
      });
      return {
        success: false,
        error: deleteErrorMessage || "Failed to delete feetype",
      };
    }
  };

  const getFeeTypeState = () => ({
    feetypes: data?.data?.items || [],
    isLoading,
    error: errorMessage,
  });

  return {
    getFeeTypeState,
    paginationData,
    useGetFeeTypeById,
    addNewFeeType,
    updateFeeType,
    deleteFeeType,
    isAdding,
    isUpdating,
    isDeleting,
    addError: addErrorMessage,
    updateError: updateErrorMessage,
    deleteError: deleteErrorMessage,
  };
};
